import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Radio, Plus, Send, ShieldAlert, Lock, X, RefreshCcw, Inbox, Link as LinkIcon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Advanced Features — Module 1: Communications Hub.
//
// Provider-neutral conversations UI. Left pane: filterable list. Right pane:
// thread view with participants, classification badge, message history, and
// compose-in-place. New Conversation button opens a modal with the required
// scaffolding to seed a first message.
//
// RESTRICTED classification is treated as a supervisory decision:
//   * Non-super_admin users cannot see or open RESTRICTED conversations.
//   * The backend enforces this; the client hides them from the list.
// ---------------------------------------------------------------------------

const KIND_LABELS = {
  internal:        "Internal",
  client_portal:   "Client Portal",
  external_email:  "External Email",
  system_notice:   "System Notice",
  secure_review:   "Secure Review",
  delivery_event:  "Delivery Event",
};

const KIND_OPTIONS = Object.keys(KIND_LABELS);

const CLASSIFICATION_STYLES = {
  STANDARD: {
    bg: "hsl(210 20% 96%)", fg: "hsl(210 15% 30%)", border: "hsl(210 15% 82%)",
  },
  CONFIDENTIAL: {
    bg: "hsl(40 55% 94%)", fg: "hsl(35 60% 30%)", border: "hsl(40 55% 78%)",
  },
  RESTRICTED: {
    bg: "hsl(0 65% 94%)", fg: "hsl(0 65% 32%)", border: "hsl(0 60% 72%)",
  },
};

function ClassificationBadge({ value }) {
  const st = CLASSIFICATION_STYLES[value] || CLASSIFICATION_STYLES.STANDARD;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
      style={{ background: st.bg, color: st.fg, border: `1px solid ${st.border}` }}
      data-testid={`comm-classification-${value}`}
    >
      {value === "RESTRICTED" && <ShieldAlert className="w-3 h-3" />}
      {value === "CONFIDENTIAL" && <Lock className="w-3 h-3" />}
      {value}
    </span>
  );
}

function StatusChip({ value }) {
  const palette = {
    open:   "bg-secondary text-foreground/80",
    draft:  "bg-secondary text-foreground/80",
    outbox: "bg-amber-100 text-amber-900 border border-amber-500/40",
    sent:   "bg-emerald-100 text-emerald-900 border border-emerald-500/40",
    closed: "bg-muted text-muted-foreground",
  };
  const cls = palette[value] || palette.open;
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${cls}`}
      data-testid={`comm-status-${value}`}
    >
      {value}
    </span>
  );
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return dt; }
}

export default function CommunicationsHub() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [fKind, setFKind] = useState("all");
  const [fClass, setFClass] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [q, setQ] = useState("");

  // New conversation modal
  const [nOpen, setNOpen] = useState(false);
  const [nSubject, setNSubject] = useState("");
  const [nKind, setNKind] = useState("internal");
  const [nClass, setNClass] = useState("STANDARD");
  const [nRecipients, setNRecipients] = useState(""); // comma-separated emails
  const [nBody, setNBody] = useState("");
  const [nBusy, setNBusy] = useState(false);

  // Compose-in-thread
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [sendingMid, setSendingMid] = useState("");

  const canRestricted = user?.role === "super_admin";

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fKind !== "all") params.kind = fKind;
      if (fClass !== "all") params.classification = fClass;
      if (fStatus !== "all") params.status = fStatus;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/communications", { params });
      setList(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load communications");
    } finally {
      setLoading(false);
    }
  }, [fKind, fClass, fStatus, q]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const r = await api.get(`/communications/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load conversation");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  async function createConversation() {
    if (!nSubject.trim()) { toast.error("Subject is required."); return; }
    setNBusy(true);
    try {
      const participants = [];
      // Self as sender
      if (user?.email) {
        participants.push({
          role: "sender", party_type: "internal_user",
          display_name: user.name || user.email, email: user.email,
          user_id: user.id || "",
        });
      }
      // External recipients from the comma-separated input (external_email flow)
      const rcpts = (nRecipients || "")
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      for (const em of rcpts) {
        participants.push({
          role: "recipient",
          party_type: (nKind === "external_email" ? "external_email" : "internal_user"),
          display_name: em, email: em,
        });
      }
      const payload = {
        subject: nSubject.trim(),
        kind: nKind,
        classification: nClass,
        participants,
        initial_message_body: nBody.trim(),
        record_links: [],
      };
      const r = await api.post("/communications", payload);
      toast.success("Conversation created");
      setNOpen(false);
      setNSubject(""); setNKind("internal"); setNClass("STANDARD");
      setNRecipients(""); setNBody("");
      await loadList();
      setSelectedId(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create conversation");
    } finally {
      setNBusy(false);
    }
  }

  async function addMessage() {
    if (!detail || !reply.trim()) return;
    setReplyBusy(true);
    try {
      const r = await api.post(`/communications/${detail.id}/messages`, {
        body: reply.trim(),
      });
      setDetail(r.data);
      setReply("");
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add message");
    } finally {
      setReplyBusy(false);
    }
  }

  async function sendMessage(mid) {
    if (!detail || !mid) return;
    setSendingMid(mid);
    try {
      const r = await api.post(`/communications/${detail.id}/messages/${mid}/send`);
      setDetail(r.data);
      await loadList();
      // Surface the specific outcome as a toast — outbox vs sent vs failed.
      const msg = (r.data.messages || []).find((m) => m.id === mid);
      const lastEvent = (msg?.delivery_events || []).slice(-1)[0];
      if (lastEvent?.event === "sent") {
        toast.success(lastEvent.detail || "Sent");
      } else if (lastEvent?.event === "outbox_held") {
        toast.warning(lastEvent.detail || "Held in Outbox");
      } else {
        toast.info(lastEvent?.event || "Recorded");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingMid("");
    }
  }

  const selected = detail;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="communications-hub">
      <div className="flex flex-wrap items-center gap-2">
        <Radio className="w-5 h-5" style={{ color: "hsl(347 60% 22%)" }} />
        <h1 className="font-display text-[22px]">Communications Hub</h1>
        <span className="text-[11px] text-muted-foreground">
          Advanced Features · Kandake owns the record. External transport is provider-neutral.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadList} data-testid="comm-refresh">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {canWrite(user) && (
            <Button size="sm" onClick={() => setNOpen(true)} data-testid="comm-new">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Conversation
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="grid md:grid-cols-[220px_180px_160px_1fr] gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Kind</Label>
              <Select value={fKind} onValueChange={setFKind}>
                <SelectTrigger className="h-8" data-testid="comm-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All kinds</SelectItem>
                  {KIND_OPTIONS.map((k) => (
                    <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Classification</Label>
              <Select value={fClass} onValueChange={setFClass}>
                <SelectTrigger className="h-8" data-testid="comm-filter-classification"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="CONFIDENTIAL">Confidential</SelectItem>
                  {canRestricted && <SelectItem value="RESTRICTED">Restricted</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Status</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="h-8" data-testid="comm-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="outbox">Outbox</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Subject contains…</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by subject…" className="h-8" data-testid="comm-filter-q" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-[360px_1fr] gap-4">
        {/* List */}
        <Card className="min-h-[400px]">
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-[13px] flex items-center gap-2">
              <Inbox className="w-4 h-4 opacity-70" />
              Conversations
              <span className="ml-auto text-[11px] text-muted-foreground" data-testid="comm-list-count">
                {list.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <div className="p-4 text-[12px] text-muted-foreground">Loading…</div>
            )}
            {!loading && list.length === 0 && (
              <div className="p-4 text-[12px] text-muted-foreground" data-testid="comm-list-empty">
                No conversations match these filters.
              </div>
            )}
            <div className="divide-y">
              {list.map((c) => {
                const on = c.id === selectedId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-secondary/60 ${on ? "bg-secondary" : ""}`}
                    data-testid={`comm-row-${c.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-[13px] truncate flex-1" title={c.subject}>
                        {c.subject || "(no subject)"}
                      </div>
                      <ClassificationBadge value={c.classification} />
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                      <span className="uppercase tracking-wide">{KIND_LABELS[c.kind] || c.kind}</span>
                      <span>·</span>
                      <StatusChip value={c.status} />
                      <span>·</span>
                      <span>{c.message_count} msg</span>
                      <span className="ml-auto">{fmt(c.last_message_at)}</span>
                    </div>
                    {(c.record_links || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.record_links.slice(0, 3).map((rl) => (
                          <span
                            key={rl.id}
                            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-secondary/60 border border-white/10"
                          >
                            <LinkIcon className="w-2.5 h-2.5" />
                            {rl.record_type}:{(rl.record_id || "").slice(0, 8)}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="min-h-[400px]">
          {!selected && (
            <CardContent className="p-6 text-[13px] text-muted-foreground" data-testid="comm-detail-empty">
              Select a conversation from the list, or start a new one.
            </CardContent>
          )}
          {selected && (
            <>
              <CardHeader className="p-3 pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-[15px] flex-1 min-w-0 truncate" data-testid="comm-detail-subject">
                    {selected.subject || "(no subject)"}
                  </CardTitle>
                  <ClassificationBadge value={selected.classification} />
                  <StatusChip value={selected.status} />
                </div>
                <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-muted-foreground">
                  <span className="uppercase tracking-wide">{KIND_LABELS[selected.kind] || selected.kind}</span>
                  <span>·</span>
                  <span>{(selected.participants || []).length} participants</span>
                  <span>·</span>
                  <span>Created {fmt(selected.created_at)}</span>
                  <span>·</span>
                  <span>Updated {fmt(selected.updated_at)}</span>
                </div>
                {(selected.participants || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(selected.participants || []).map((p) => (
                      <Badge
                        key={p.id}
                        variant="outline"
                        className="text-[10.5px] font-normal"
                        data-testid={`comm-participant-${p.id}`}
                      >
                        <span className="uppercase tracking-wide mr-1 opacity-70">{p.role}</span>
                        {p.display_name || p.email || p.user_id || "?"}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-3 pt-1 space-y-3">
                {detailLoading && (
                  <div className="text-[12px] text-muted-foreground">Loading messages…</div>
                )}
                {(selected.messages || []).map((m) => {
                  const lastEvent = (m.delivery_events || []).slice(-1)[0];
                  return (
                    <div
                      key={m.id}
                      className="rounded border bg-background p-3"
                      data-testid={`comm-message-${m.id}`}
                    >
                      <div className="flex items-baseline gap-2 text-[11.5px]">
                        <span className="font-medium">{m.author_display || m.author_email || "unknown"}</span>
                        <span className="text-muted-foreground">· {fmt(m.created_at)}</span>
                        {m.sent_at && (
                          <span className="ml-2 text-emerald-800/80">sent {fmt(m.sent_at)}</span>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed">
                        {m.body}
                      </div>
                      {(m.attachments || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {m.attachments.map((a) => (
                            <span
                              key={a.id}
                              className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded bg-secondary/70 border border-white/10"
                            >
                              {a.filename || "attachment"}
                              <ClassificationBadge value={a.classification} />
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {m.is_draft && canWrite(user) && (
                          <Button
                            size="sm"
                            onClick={() => sendMessage(m.id)}
                            disabled={sendingMid === m.id}
                            data-testid={`comm-message-send-${m.id}`}
                            style={{ background: "hsl(347 72% 22%)" }}
                          >
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            {sendingMid === m.id ? "Sending…" : "Send"}
                          </Button>
                        )}
                        {lastEvent && (
                          <span
                            className="text-[11px] text-muted-foreground"
                            data-testid={`comm-message-last-event-${m.id}`}
                          >
                            <span className="font-mono uppercase tracking-wide">
                              {lastEvent.event}
                            </span>
                            {lastEvent.provider && (
                              <> · via <span className="font-mono">{lastEvent.provider}</span></>
                            )}
                            {lastEvent.detail && <> — {lastEvent.detail}</>}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(selected.messages || []).length === 0 && (
                  <div className="text-[12px] text-muted-foreground" data-testid="comm-thread-empty">
                    No messages yet.
                  </div>
                )}

                {canWrite(user) && (
                  <div className="mt-2 rounded border bg-secondary/40 p-3">
                    <Label className="text-[11px] uppercase tracking-wide">Add message</Label>
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      rows={3}
                      placeholder="Write a message… it will be added as a draft. Click Send on the draft when ready."
                      className="mt-1 text-[13px]"
                      data-testid="comm-reply-body"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={addMessage}
                        disabled={!reply.trim() || replyBusy}
                        data-testid="comm-reply-submit"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        {replyBusy ? "Adding…" : "Add draft"}
                      </Button>
                      <span className="text-[10.5px] text-muted-foreground">
                        RESTRICTED conversations and RESTRICTED attachments cannot be transmitted by ordinary external email.
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* New conversation modal */}
      <Dialog open={nOpen} onOpenChange={setNOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="comm-new-dialog">
          <DialogHeader>
            <DialogTitle>New Conversation</DialogTitle>
            <DialogDescription>
              Kandake owns the record. If you choose <strong>External Email</strong> and no transport is configured, the message will be held in the Outbox rather than pretending it was sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wide">Subject</Label>
              <Input value={nSubject} onChange={(e) => setNSubject(e.target.value)} className="mt-1" data-testid="comm-new-subject" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Kind</Label>
                <Select value={nKind} onValueChange={setNKind}>
                  <SelectTrigger className="mt-1" data-testid="comm-new-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map((k) => (
                      <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Classification</Label>
                <Select value={nClass} onValueChange={setNClass}>
                  <SelectTrigger className="mt-1" data-testid="comm-new-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="CONFIDENTIAL">Confidential</SelectItem>
                    {canRestricted && <SelectItem value="RESTRICTED">Restricted (Super Admin)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {nKind === "external_email" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  Recipient email(s) — comma separated
                </Label>
                <Input
                  value={nRecipients}
                  onChange={(e) => setNRecipients(e.target.value)}
                  placeholder="e.g. reviewer@label.com, supervisor@example.com"
                  className="mt-1 font-mono"
                  data-testid="comm-new-recipients"
                />
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wide">
                First message <span className="normal-case text-muted-foreground">(optional — seeds a draft)</span>
              </Label>
              <Textarea
                value={nBody}
                onChange={(e) => setNBody(e.target.value)}
                rows={4}
                className="mt-1 text-[13px]"
                data-testid="comm-new-body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNOpen(false)} data-testid="comm-new-cancel">Cancel</Button>
            <Button
              onClick={createConversation}
              disabled={!nSubject.trim() || nBusy}
              data-testid="comm-new-submit"
              style={{ background: "hsl(347 72% 22%)" }}
            >
              {nBusy ? "Creating…" : "Create conversation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
